import React, { useState, useEffect } from "react";
import { FiX, FiCheck } from "react-icons/fi";
import { toast } from "react-toastify";

import ApproverPicker from "../../../Common/ApproverPicker";

const EditRequisitionModal = ({ open, onClose, requisition, onSave }) => {
  const [formData, setFormData] = useState({
    project: "",
    requestedBy: "",
    requiredBy: "",
    priority: "Medium",
    approvalStatus: "Pending Approval",
    notes: "",
  });
  const [items, setItems] = useState([]);
  const [approverIds, setApproverIds] = useState([]);

  useEffect(() => {
    if (requisition) {
      setFormData({
        project: requisition.project || "",
        requestedBy: requisition.requestedBy || "",
        requiredBy: requisition.requiredBy || "",
        priority: requisition.priority || "Medium",
        approvalStatus: requisition.approvalStatus || "Pending Approval",
        notes: requisition.notes || "",
      });
      setItems((requisition.items || []).map((i) => ({ ...i })));
      setApproverIds(requisition.approverIds || []);
    }
  }, [requisition]);

  if (!open || !requisition) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const estCost = items.reduce(
    (sum, i) => sum + (Number(i.qty) || 0) * (Number(i.unitPrice) || 0),
    0,
  );

  const setRate = (id, value) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, unitPrice: value } : i)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (approverIds.length === 0) {
      toast.error("Select at least one approver");
      return;
    }

    try {
      await onSave({ ...requisition, items, approverIds, estCost });
      toast.success(`Requisition ${requisition.reqNo} updated successfully!`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="req-modal-overlay" onClick={onClose}>
      <div className="req-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="req-modal-header">
          <h5 className="req-modal-title m-0">Edit Requisition: {requisition.reqNo}</h5>
          <button type="button" className="req-modal-close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="req-modal-body">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Project / Site
                </label>
                <input
                  type="text"
                  name="project"
                  className="filter-control-input"
                  value={formData.project}
                  onChange={handleChange}
                  disabled
                />
              </div>

              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Requested By
                </label>
                <input
                  type="text"
                  name="requestedBy"
                  className="filter-control-input"
                  value={formData.requestedBy}
                  onChange={handleChange}
                  disabled
                />
              </div>

              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Required By Date
                </label>
                <input
                  type="text"
                  name="requiredBy"
                  className="filter-control-input"
                  placeholder="DD/MM/YYYY"
                  value={formData.requiredBy}
                  onChange={handleChange}
                  disabled
                />
              </div>

              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Estimated Cost (PKR)
                </label>
                <input
                  type="number"
                  name="estCost"
                  className="filter-control-input"
                  value={estCost}
                  readOnly
                />
              </div>

              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Priority
                </label>
                <select
                  name="priority"
                  className="filter-control-select"
                  value={formData.priority}
                  onChange={handleChange}
                  disabled
                >
                  <option value="High / Urgent">High / Urgent</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div>
                <label
                  className="filter-label-text"
                  style={{ marginBottom: "4px", display: "block" }}
                >
                  Approval Status
                </label>
                <select
                  name="approvalStatus"
                  className="filter-control-select"
                  value={formData.approvalStatus}
                  onChange={handleChange}
                  disabled
                >
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Partially Ordered">Partially Ordered</option>
                  <option value="Ordered">Ordered</option>
                  <option value="Closed">Closed</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label
                className="filter-label-text"
                style={{ marginBottom: "4px", display: "block" }}
              >
                Items / Materials Requested (edit the estimated rate)
              </label>
              <table style={{ width: "100%", fontSize: "12.5px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Material</th>
                    <th style={{ textAlign: "center" }}>Qty</th>
                    <th style={{ textAlign: "right" }}>Est. Rate (PKR)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td>{i.materialName}</td>
                      <td style={{ textAlign: "center" }}>
                        {i.qty} {i.uom}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <input
                          type="number"
                          min="0"
                          className="filter-control-input"
                          style={{ width: "120px", marginLeft: "auto" }}
                          value={i.unitPrice}
                          onChange={(e) => setRate(i.id, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label
                className="filter-label-text"
                style={{ marginBottom: "4px", display: "block" }}
              >
                Approvers
              </label>
              <ApproverPicker
                projectId={requisition.projectId}
                value={approverIds}
                onChange={setApproverIds}
              />
            </div>

            <div>
              <label
                className="filter-label-text"
                style={{ marginBottom: "4px", display: "block" }}
              >
                Remarks / Instructions
              </label>
              <textarea
                name="notes"
                className="filter-control-input"
                style={{ height: "55px", padding: "8px 12px", resize: "vertical" }}
                value={formData.notes}
                onChange={handleChange}
                disabled
              />
            </div>
          </div>

          <div className="req-modal-footer">
            <button
              type="button"
              className="btn_secondary"
              onClick={onClose}
              style={{ height: "36px", padding: "0 18px", borderRadius: "8px", fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn_primary"
              style={{
                height: "36px",
                padding: "0 18px",
                borderRadius: "8px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FiCheck /> Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRequisitionModal;
